import { Form } from 'antd';
import React, { memo, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import DefaultForm from 'components/composites/DefaultForm';
import DefaultFormItem from 'components/composites/DefaultFormItem';
import BackOfficeDrawer from 'components/drawers/BackOfficeDrawer';
import { BackOfficeInvitationFormItems } from 'components/forms/BackOfficeInvitationFormItems';
import { InternalAdminRoleFieldValue } from 'components/forms/BackOfficeUserFormItems/components/InternalAdminRoleFieldValue';
import { InternalAdminRoleSelect } from 'components/forms/BackOfficeUserFormItems/components/InternalAdminRoleSelect';
import FormItemEmail from 'components/forms/FormItemEmail';
import { useBackOfficeInvitationsRequestParameters } from 'components/requests/useBackOfficeRequestParameters';
import { InternalAdminRole } from 'constants/internal-admin-roles';
import { SEARCH_PARAMS } from 'constants/search-params';
import { addBackOfficeInvitation, loadBackOfficeInvitations } from 'store/modules/backOfficeInvitations/actions';
import ActionType from 'store/modules/backOfficeInvitations/constants';
import { makeSelectInvitationsByEmail } from 'store/modules/backOfficeInvitations/selectors';
import clearErrors from 'store/modules/error/actions';
import { useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import BackOfficeInvitation, { NewBackOfficeInvitation } from 'types/models/back-office-invitation';
import { checkIsInternalAdmin } from 'utils/AccessControl/check-is-internal-admin';

const actions = [ActionType.ADD_BACK_OFFICE_INVITATION_REQUEST];

function BackOfficeUserCreateDrawer(): JSX.Element | null {
  const [form] = Form.useForm<NewBackOfficeInvitation>();
  const [searchParams, setSearchParams] = useSearchParams();
  const drawer: string | null = searchParams.get(SEARCH_PARAMS.DRAWER.key);
  const invitationEmail: string | null = searchParams.get(SEARCH_PARAMS.INVITATION_EMAIL);
  const isViewingInvitee: boolean = !!invitationEmail;

  const visible = drawer === SEARCH_PARAMS.DRAWER.values.ADD_USER || isViewingInvitee;

  const initialValues = invitationEmail
    ? { email: invitationEmail } // in the InviteeEdit drawer
    : { adminRole: InternalAdminRole._NONE }; // in the UserCreate drawer

  const existingInvitations: BackOfficeInvitation[] = useSelector(
    makeSelectInvitationsByEmail(invitationEmail ?? ''),
    shallowEqual
  );
  const hasInternalAdminInvitation: boolean = existingInvitations.some((invitation) => checkIsInternalAdmin(invitation));

  const canCreateInternalUsers = useMyUserHasInternalPermission({
    resource: ProtectedResource.INTERNAL_USER,
    action: ResourceAction.ADD
  });

  const dispatch = useDispatch();
  useEffect(() => {
    if (visible) {
      dispatch(clearErrors(actions));
      form.resetFields();
    }
  }, [dispatch, form, visible, drawer, invitationEmail]);

  const handleClose = () => {
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev);
      newSearchParams.delete(SEARCH_PARAMS.DRAWER.key); // Close drawer
      newSearchParams.delete(SEARCH_PARAMS.INVITATION_EMAIL); // Unselect the active invitee
      return newSearchParams;
    });
  };

  const requestParameters = useBackOfficeInvitationsRequestParameters();

  const onFooterSubmitSuccess = () => {
    handleClose();
    dispatch(loadBackOfficeInvitations(requestParameters));
  };

  const createNewInviteeAndCloseDrawer = () => {
    form.validateFields()
      .then((values) => {
        if (values.adminRole && checkIsInternalAdmin({ adminRole: values.adminRole })) {
          // Only allow adding enterprise membership if user is not an internal admin
          delete values.enterpriseId;
          delete values.role;
        }
        dispatch(addBackOfficeInvitation(values, onFooterSubmitSuccess));
      })
      .catch((e) => { });
  };

  return (
    <BackOfficeDrawer
      visible={visible}
      backOfficeSection="Invite User"
      actions={actions}
      handleClose={handleClose}
      handleSave={createNewInviteeAndCloseDrawer}
      noFooter={isViewingInvitee}
      footerLabel="Invite"
    >
      <DefaultForm
        layout="vertical"
        form={form}
        initialValues={initialValues}
      >
        <FormItemEmail
          readOnly={!!initialValues?.email}
        />
        {/* Only allow selecting an Internal Admin Role when creating a new user.
            For existing invitees, adminRole is set per invitation and cannot be changed here. */}
        {canCreateInternalUsers && !isViewingInvitee && (
          <DefaultFormItem name="adminRole" label="Internal Admin Role" required>
            <InternalAdminRoleSelect />
          </DefaultFormItem>
        )}
        <InternalAdminRoleFieldValue>
          {/* Only allow adding enterprise membership if user is not an internal admin */}
          {(userIsInternalAdmin: boolean) => (
            <BackOfficeInvitationFormItems
              onInlineSubmitSuccess={handleClose}
              disabled={userIsInternalAdmin || hasInternalAdminInvitation}
              // Invitation list is not relevant to a non-existing user. Only show it when viewing an existing invitee.
              showInvitationList={isViewingInvitee}
              // When viewing an existing invitee, the drawer's INVITE button is hidden as it is for creating new users.
              // New invitations must be created using the InvitationFormItems' INVITE button instead.
              showInviteButton={isViewingInvitee}
            />
          )}
        </InternalAdminRoleFieldValue>
      </DefaultForm>
    </BackOfficeDrawer>
  );
}

export default memo(BackOfficeUserCreateDrawer);
